import { html, nothing } from 'lit';
import { classMap } from 'lit-html/directives/class-map';
import componentStyles from './location-card.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';

export default class LocationCard extends OutlineElement {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      /**
       * location page details link
       */
      detailsLink: { type: String },
      /**
       * location image source
       *  */
      imageSrc: { type: String },
      /**
       * location image alt text
       */
      imageAlt: { type: String },
      /**
       * location title
       */
      title: { type: String },
      /**
       * location address text
       */
      addressText: { type: String },
      /**
       * location address link
       */
      addressLink: { type: String },
      addressLine1: { type: String },
      addressLine2: { type: String },
      /**
       * location phone number
       */
      phoneNumber: { type: String },
      /**
       * location phone text
       */
      phoneText: { type: String },
      /**
       * styleOverride phone text
       */
      styleOverride: { type: String },
      /**
       * location card type 'map' 'list'
       */
      cardType: { type: String },
      /**
       * location onViewMap callback function (should already be initialize with a unique id)
       */
      onViewMap: { type: Function },
      /**
       * location { latitude: Number, longitude: Number }
       */
      location: { type: Object },
      id: { type: String },
      number: { type: Number },
      selected: { type: Boolean },
      /**
       * Google maps location URL
       */
      locationUrl: { type: String },
    };
  }

  constructor() {
    super();
    this.selected = false;
    this.number = 0;
    this.detailsLink = '';
    this.addressLink = '';
    this.addressText = '';
    this.addressLine1 = '';
    this.addressLine2 = '';
    this.phoneNumber = '';
    this.phoneText = '';
    this.title = '';
    this.imageSrc = '';
    this.imageAlt = '';
    this.styleOverride = '';
    this.cardType = 'list';
    this.id = '';
    this.location = {};
    this.locationUrl = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.locationMapSelectCardHandler = this.locationMapSelectCard.bind(this);
    window.addEventListener(
      'hf-location-map-select',
      this.locationMapSelectCardHandler
    );
    this.resetSelectedCardHandler = this.resetSelectedCard.bind(this);
    window.addEventListener(
      'hf-location-list-select',
      this.resetSelectedCardHandler
    );
  }

  resetSelectedCard() {
    this.selected = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(
      'hf-location-map-select',
      this.locationMapSelectCard
    );
    this.resetSelectedCardHandler = this.resetSelectedCard.bind(this);
    window.removeEventListener(
      'hf-location-list-select',
      this.resetSelectedCardHandler
    );
  }

  locationMapSelectCard(event) {
    this.selected = event.detail.id === this.id;
  }

  generateLinkList() {
    const data = [];

    if (
      this.phoneNumber &&
      this.phoneNumber !== 'false' &&
      this.phoneNumber !== 'FALSE'
    ) {
      const phoneData = {
        text: this.phoneText,
        url: `tel:${this.phoneNumber}`,
        iconname: 'phone',
        label: Drupal.t('Click to Call'),
      };

      data.unshift(phoneData);
    }

    if (this.cardType === 'list') {
      const mapLink = {
        text: Drupal.t('Directions'),
        label: '3.25 MI',
        url: this.locationUrl,
        iconname: 'location',
      };
      if (this.onViewMap) {
        mapLink.onClick = () =>
          this.onViewMap({
            location: this.location,
            id: this.id,
          });
      }

      data.unshift(mapLink);
    }
    return data;
  }

  handleCardClick() {
    if (this.cardType !== 'map') {
      const mapEvent = new CustomEvent('hf-location-list-select', {
        detail: {
          id: this.id,
          location: this.location,
        },
      });
      window.dispatchEvent(mapEvent);
      this.selected = true;
    }
  }

  render() {
    const linkList = this.generateLinkList();

    return html`
      <div
        @click="${this.handleCardClick}"
        class="${classMap({
          'location-card': true,
          'location-card--selected': this.selected,
          [`card--${this.cardType}`]: true,
          [this.styleOverride]: !!this.styleOverride,
        })}"
      >
        ${this.cardType === 'map' ? html`<slot></slot>` : nothing}
        ${this.cardType !== 'map' && this.number !== 0
          ? html`<div class="location-card__number">${this.number}.</div>`
          : nothing}
        ${this.imageSrc != ''
          ? html` <hf-link
              href="${this.detailsLink}"
              styleclass="location-card-${this.cardType}${this.styleOverride}"
            >
              <img
                src="${this.imageSrc}"
                alt="${this.imageAlt}"
                class="location-card__image-${this.cardType}"
              />
            </hf-link>`
          : nothing}
        <div class="location-card__body-${this.cardType}">
          <hf-link href="${this.detailsLink}">
            <hf-heading level="5" levelstyle="location-card-${this.cardType}">
              ${this.title}
            </hf-heading>
          </hf-link>
          ${this.cardType === 'map'
            ? html`
                <hf-paragraph small styleclass="location-card-map">
                  ${this.addressLine1}<br />
                  ${this.addressLine2 !== ''
                    ? html`${this.addressLine2}<br />`
                    : nothing}
                  ${this.addressText}<br />
                  ${this.phoneNumber}
                </hf-paragraph>

                <hf-link
                  href="${this.detailsLink}"
                  styleclass="location-card-directions"
                  class="location-card__link"
                >
                  Get directions
                  <hf-icon
                    name="arrow-right"
                    class="location-card__icon"
                  ></hf-icon>
                </hf-link>
              `
            : html`<hf-paragraph small styleclass="location-card">
                  ${this.addressLine1}<br />
                  ${this.addressLine2 !== ''
                    ? html`${this.addressLine2}<br />`
                    : nothing}
                  ${this.addressText}
                </hf-paragraph>
                <hf-icon-nav
                  .links="${linkList}"
                  title="${this.title} nav"
                  listDirection="always-horizontal"
                  navClass="location-card"
                  hasPadding="false"
                  hasSeparator="true"
                ></hf-icon-nav>`}
        </div>
      </div>
    `;
  }
}

customElements.define('hf-location-card', LocationCard);
